import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/admin';
import { useAuth } from '../../context/auth';
import {
  NAME_MAX,
  assignableRoles,
  assignableWells,
  describeReach,
  mayManageUser,
  visibleUsers,
} from '../../lib/access';
import type { AdminUser, AdminWell, Company, Role } from '../../lib/access';
import { deleteUser, saveUser } from '../../lib/api/admin';
import type { UserInput } from '../../lib/api/admin';
import { useQueryNav } from '../../lib/nav';
import { ActionMessages, AdminShell } from './AdminShell';
import { ACTIVE_STATUSES, plural, usePaging, useView } from './listing';
import {
  ActivePill,
  Avatar,
  DeleteLink,
  Dialog,
  DialogButtons,
  DialogError,
  ListFoot,
  StatusChoice,
  ViewToggle,
} from './parts';
import { useAdminAction } from './useAdminAction';

const EMPTY: UserInput = { name: '', email: '', companyId: null, roleId: null, wellIds: [], active: true };

/**
 * The people. Who may create whom goes one step at a time: the superadmin
 * creates Company Admins, a Company Admin creates Well Admins, a Well Admin
 * creates the people who work on the well. Each sees only their own company.
 *
 * A user has one role in one company, and the wells picked for them.
 */
export function Users() {
  const { user: me } = useAuth();
  const { store, allWells, access, previewAs } = useAdmin();
  const navigate = useNavigate();
  const { params, set } = useQueryNav();
  const { busy, error, notice, run, clear } = useAdminAction();
  const [view, setView] = useView();

  const isSuperadmin = access.kind === 'superadmin';
  const roles = assignableRoles(access, store.roles);

  // A link elsewhere can arrive here with the form already filled in:
  // ?new=1&company=…&role=…  The request is read once, as the page opens, and
  // then taken out of the URL so a refresh does not reopen the form.
  const wantsNew = params.get('new') === '1';
  const askedCompany = Number(params.get('company')) || null;
  const askedRole = Number(params.get('role')) || null;

  /** null: closed. 0: a new user. Otherwise the id of the account being edited. */
  const [editing, setEditing] = useState<number | null>(wantsNew ? 0 : null);
  const [asked] = useState(wantsNew ? { companyId: askedCompany, roleId: askedRole } : null);
  const [company, setCompany] = useState<number | 'all'>((wantsNew && askedCompany) || 'all');
  const [roleFilter, setRoleFilter] = useState<number | 'all'>('all');
  const [status, setStatus] = useState<'all' | 'active' | 'off'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (wantsNew) set({ new: null, company: null, role: null }, { replace: true });
  }, [wantsNew, set]);

  const companyOf = (id: number) => store.companies.find((c) => c.id === id);
  const companyName = (id: number) => companyOf(id)?.name ?? '—';
  /** Switched off themselves, or through their company. */
  const isOff = (person: AdminUser) => !person.active || !companyOf(person.companyId)?.active;

  const visible = visibleUsers(access, store.users);
  const filtered = visible.filter((u) => {
    if (company !== 'all' && u.companyId !== company) return false;
    if (roleFilter !== 'all' && u.roleId !== roleFilter) return false;
    if (status !== 'all' && isOff(u) !== (status === 'off')) return false;
    const q = search.trim().toLowerCase();
    return !q || u.name.toLowerCase().includes(q) || u.email.includes(q);
  });
  const paging = usePaging(filtered);
  const filter = (apply: () => void) => {
    apply();
    paging.first();
  };

  const current = editing ? store.users.find((u) => u.id === editing) : undefined;
  const noCompanies = store.companies.length === 0;

  function open(target: AdminUser | null) {
    clear();
    setEditing(target?.id ?? 0);
  }

  /** What a new user's form starts from. */
  const fresh: UserInput = {
    ...EMPTY,
    // Below the superadmin there is only one company to pick: your own.
    companyId: asked?.companyId ?? access.companyId ?? (company === 'all' ? (store.companies[0]?.id ?? null) : company),
    roleId: asked?.roleId ?? roles[0]?.id ?? null,
  };

  async function onSave(draft: UserInput) {
    const id = editing || null;
    const role = store.roles.find((r) => r.id === draft.roleId);
    const ok = await run(
      () =>
        saveUser(id, draft, {
          actor: access.subject?.email ?? me?.email ?? 'unknown',
          companyWellIds: allWells.filter((w) => w.companyId === draft.companyId).map((w) => w.id),
        }),
      id ? `${draft.name.trim()} saved.` : `${draft.name.trim()} created as ${role?.name ?? 'user'}.`,
    );
    if (ok) setEditing(null);
  }

  async function onDelete(target: AdminUser) {
    const ok = await run(() => deleteUser(target.id), `${target.name} deleted.`);
    if (ok) setEditing(null);
  }

  function viewAs(target: AdminUser) {
    previewAs(target.id);
    navigate('/web');
  }

  const roleOf = (person: AdminUser) => store.roles.find((r) => r.id === person.roleId);

  const roleTag = (person: AdminUser) => {
    const theirs = roleOf(person);
    return theirs ? <span className="cmtag">{theirs.name}</span> : <span className="cmnone">Role deleted</span>;
  };

  const nameCell = (person: AdminUser) => (
    <span className="cmadmin">
      <Avatar id={person.id} name={person.name} />
      {mayManageUser(access, person, store, allWells) ? (
        <button type="button" className="cmadmin-name" title={`Edit ${person.name}`} onClick={() => open(person)}>
          {person.name}
        </button>
      ) : (
        <span>{person.name}</span>
      )}
      {person.id === access.subject?.id && <span className="cmtag cmtag--plain">You</span>}
    </span>
  );

  const actions = (person: AdminUser) => {
    const off = isOff(person);
    return (
      <>
        {mayManageUser(access, person, store, allWells) && (
          <button type="button" className="cmbtn cmbtn--small" onClick={() => open(person)}>
            Edit
          </button>
        )}
        {isSuperadmin && (
          <button
            type="button"
            className="cmbtn cmbtn--small"
            onClick={() => viewAs(person)}
            disabled={off}
            title={off ? 'Switched off: there is nothing to see' : `See the portal as ${person.name} does`}
          >
            View as
          </button>
        )}
        {isSuperadmin && (
          <Link
            className="cmbtn cmbtn--small"
            to={`/web/Admin/AuditLogs?email=${encodeURIComponent(person.email)}`}
            title={`What ${person.name} did: sign-ins, API calls, errors`}
          >
            Audit log
          </Link>
        )}
      </>
    );
  };

  return (
    <AdminShell
      active="Users"
      title="User Management"
      count={`${visible.length} ${plural(visible.length, 'User')}`}
      action={
        <>
          {isSuperadmin && (
            <Link className="cmbtn" to="/web/Admin/AuditLogs" title="Every user's activity, and failed sign-ins from unknown addresses">
              Audit log (all)
            </Link>
          )}
          <button
            type="button"
            className="cmbtn cmbtn--primary"
            onClick={() => open(null)}
            disabled={noCompanies || roles.length === 0}
            title={noCompanies ? 'Create a company first' : roles.length === 0 ? 'There is no role you can hand out' : undefined}
          >
            + Add User
          </button>
        </>
      }
    >
      <div className="cmtools">
        <input
          type="search"
          className="cmsearch"
          placeholder="Search name or email…"
          aria-label="Search name or email"
          value={search}
          onChange={(e) => filter(() => setSearch(e.target.value))}
        />
        {isSuperadmin && (
          <select
            className="cmselect"
            aria-label="Company"
            value={String(company)}
            onChange={(e) => filter(() => setCompany(e.target.value === 'all' ? 'all' : Number(e.target.value)))}
          >
            <option value="all">Company: All</option>
            {store.companies.map((c) => (
              <option key={c.id} value={c.id}>
                Company: {c.name}
              </option>
            ))}
          </select>
        )}
        <select
          className="cmselect"
          aria-label="Role"
          value={String(roleFilter)}
          onChange={(e) => filter(() => setRoleFilter(e.target.value === 'all' ? 'all' : Number(e.target.value)))}
        >
          <option value="all">Role: All</option>
          {store.roles.map((r) => (
            <option key={r.id} value={r.id}>
              Role: {r.name}
            </option>
          ))}
        </select>
        <select
          className="cmselect"
          aria-label="Status"
          value={status}
          onChange={(e) => filter(() => setStatus(e.target.value as 'all' | 'active' | 'off'))}
        >
          <option value="all">Status: All</option>
          <option value="active">Status: Active</option>
          <option value="off">Status: Inactive</option>
        </select>

        <span className="cmspacer" />
        <ViewToggle view={view} onChange={setView} />
      </div>

      <ActionMessages error={error} notice={notice} inDialog={editing !== null} />

      {!isSuperadmin && (
        <p className="cmhint cmmsg">
          The people of {companyName(access.companyId ?? 0)} you look after. You can create{' '}
          {roles.length > 0 ? roles.map((r) => r.name).join(', ') : 'no roles'}.
        </p>
      )}

      {noCompanies && (
        <p className="cmempty">
          A user belongs to a company. Create the company first, on the <Link to="/web/Admin/Companies">Companies</Link> tab.
        </p>
      )}
      {!noCompanies && visible.length === 0 && <p className="cmempty">No users yet.</p>}
      {visible.length > 0 && filtered.length === 0 && <p className="cmempty">No user matches that search.</p>}

      {paging.shown.length > 0 && view === 'list' && (
        <div className="cmtable-wrap">
          <table className="cmtable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                {isSuperadmin && <th>Company</th>}
                <th>Role</th>
                <th>Wells</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paging.shown.map((person) => (
                <tr key={person.id} className={isOff(person) ? 'is-off' : undefined}>
                  <td>{nameCell(person)}</td>
                  <td>{person.email}</td>
                  {isSuperadmin && <td>{companyName(person.companyId)}</td>}
                  <td>{roleTag(person)}</td>
                  <td className="cmwrap">{describeReach(person, roleOf(person), allWells)}</td>
                  <td>
                    <ActivePill active={!isOff(person)} />
                  </td>
                  <td>
                    <span className="cmactions">{actions(person)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paging.shown.length > 0 && view === 'grid' && (
        <div className="cmgrid">
          {paging.shown.map((person) => (
            <article key={person.id} className={isOff(person) ? 'cmcard is-off' : 'cmcard'}>
              <header>
                <span>
                  {nameCell(person)}
                  <small>{person.email}</small>
                </span>
                <ActivePill active={!isOff(person)} />
              </header>
              <dl>
                {isSuperadmin && (
                  <div>
                    <dt>Company</dt>
                    <dd>{companyName(person.companyId)}</dd>
                  </div>
                )}
                <div>
                  <dt>Role</dt>
                  <dd>{roleTag(person)}</dd>
                </div>
                <div>
                  <dt>Wells</dt>
                  <dd>{describeReach(person, roleOf(person), allWells)}</dd>
                </div>
              </dl>
              <footer className="cmactions">{actions(person)}</footer>
            </article>
          ))}
        </div>
      )}

      <ListFoot paging={paging} one="user" />

      {editing !== null && (
        <UserDialog
          key={editing}
          target={current ?? null}
          start={fresh}
          companies={store.companies}
          roles={roles}
          wellsOf={(companyId) => assignableWells(access, allWells, companyId)}
          pickCompany={isSuperadmin}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSave={(draft) => void onSave(draft)}
          onDelete={current ? () => void onDelete(current) : undefined}
        />
      )}
    </AdminShell>
  );
}

function UserDialog({
  target,
  start,
  companies,
  roles,
  wellsOf,
  pickCompany,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  target: AdminUser | null;
  start: UserInput;
  companies: Company[];
  /** The roles this person may hand out. */
  roles: Role[];
  /** The wells this person may put a user on, within one company. */
  wellsOf: (companyId: number) => AdminWell[];
  pickCompany: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: UserInput) => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<UserInput>(() =>
    target
      ? {
          name: target.name,
          email: target.email,
          companyId: target.companyId,
          roleId: target.roleId,
          wellIds: [...target.wellIds],
          active: target.active,
        }
      : start,
  );

  const role = roles.find((r) => r.id === draft.roleId);
  const company = companies.find((c) => c.id === draft.companyId);
  const pickable = draft.companyId == null ? [] : wellsOf(draft.companyId);

  function toggleWell(id: number, on: boolean) {
    setDraft((d) => {
      if (role?.scope === 'one') return { ...d, wellIds: on ? [id] : [] };
      return { ...d, wellIds: on ? [...d.wellIds, id] : d.wellIds.filter((w) => w !== id) };
    });
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave(draft);
  }

  return (
    <Dialog
      title={target ? 'Edit User' : 'Add User'}
      subtitle={target ? `${company?.name ?? '—'} · ${target.name}` : company?.name}
      size="mid"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} label={target ? 'Save Changes' : 'Add User'} onClose={onClose} />}
    >
      <DialogError error={error} />

      <div className="cmfields">
        <label className="cmfield">
          <span>Full Name *</span>
          <input
            type="text"
            value={draft.name}
            maxLength={NAME_MAX}
            required
            autoFocus
            placeholder="e.g. Budi Santoso"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="cmfield">
          <span>Work Email *</span>
          <input
            type="email"
            value={draft.email}
            required
            readOnly={target !== null}
            placeholder="name@company.com"
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
          />
          <small>{target ? 'How they sign in. Cannot be changed.' : 'How they sign in. Fixed once the account exists.'}</small>
        </label>
        <label className="cmfield">
          <span>Company *</span>
          <select
            value={draft.companyId ?? ''}
            required
            disabled={!pickCompany}
            onChange={(e) => setDraft({ ...draft, companyId: Number(e.target.value) || null, wellIds: [] })}
          >
            <option value="">Pick a company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="cmfield">
          <span>Role *</span>
          <select
            value={draft.roleId ?? ''}
            required
            onChange={(e) => setDraft({ ...draft, roleId: Number(e.target.value) || null, wellIds: [] })}
          >
            <option value="">Pick a role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {role?.description && <small>{role.description}</small>}
        </label>
      </div>

      <fieldset className="cmgroup">
        <legend>Wells</legend>
        {!role && <p className="cmhint">Pick a role to see which wells it takes.</p>}
        {role?.scope === 'none' && <p className="cmhint">{role.name} opens no well, so there is nothing to pick.</p>}
        {role?.scope === 'all' && (
          <p className="cmhint">
            {role.name} reaches every well of {company?.name ?? 'the company'}, including wells added later.
          </p>
        )}
        {role && (role.scope === 'one' || role.scope === 'many') && (
          <>
            <p className="cmhint">
              {role.scope === 'one' ? `${role.name} is given exactly one well.` : `Pick every well ${role.name} works on.`}
            </p>
            {pickable.length === 0 && (
              <p className="cmnone">
                {company ? `${company.name} has no wells yet. Give it one on the Wells tab first.` : 'Pick a company to see its wells.'}
              </p>
            )}
            <div className="cmchecks">
              {pickable.map((well) => (
                <label key={well.id} className="cmcheck">
                  <input
                    type={role.scope === 'one' ? 'radio' : 'checkbox'}
                    name="user-wells"
                    checked={draft.wellIds.includes(well.id)}
                    onChange={(e) => toggleWell(well.id, e.target.checked)}
                  />
                  <span>
                    {well.name} {well.field && <small>{well.field}</small>}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
      </fieldset>

      <StatusChoice
        legend={target ? 'Status' : 'Initial Status'}
        name="user-status"
        options={ACTIVE_STATUSES}
        value={draft.active ? 'active' : 'inactive'}
        onChange={(next) => setDraft({ ...draft, active: next === 'active' })}
      />

      <p className="cminfo">Switched to Inactive, the account stays, with its history, but opens nothing.</p>

      {target && onDelete && (
        <DeleteLink what="account" question={`Delete ${target.name}’s account?`} busy={busy} onDelete={onDelete} />
      )}
    </Dialog>
  );
}
